import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, useCallback, useEffect } from "react";
import { z } from "zod";

import { ErrorBoundary } from "@/components/error-boundary";
import { useLeaveMbcFlow } from "@/features/request/mbc-chat/hooks/useLeaveMbcFlow";
import { requestIdFromSessionId } from "@/features/request/mbc-chat/lib/session-ids";
import { MBCReview } from "@/features/request/mbc-review";
import { useGetRequest } from "@/hooks/useGetRequest";
import { requireMbcFeatureFlag } from "@/lib/posthog-flags";

const mbcReviewSearchSchema = z.object({
  brandId: z.coerce.number().optional(),
  designType: z.coerce.number().optional(),
  ["project-id"]: z.coerce.number().optional(),
  ["request-type"]: z
    .enum(["video", "graphic", "website_design", "website_implementation"])
    .optional(),
  resourceId: z.string().optional(),
  threadId: z.string().optional(),
});

export const Route = createFileRoute("/_auth/_app-shell/requests/mbc-review")({
  beforeLoad: requireMbcFeatureFlag,
  component: RouteComponent,
  validateSearch: mbcReviewSearchSchema,
});

function RouteComponent() {
  const navigate = useNavigate();
  const {
    brandId,
    designType,
    ["project-id"]: projectId,
    ["request-type"]: requestType,
    resourceId,
    threadId,
  } = Route.useSearch();

  const requestId = threadId ? requestIdFromSessionId(threadId) : null;
  const { data: request } = useGetRequest(requestId ?? undefined);

  useEffect(() => {
    if (requestId == null || request?.isDraft !== false) return;

    void navigate({
      params: { requestId },
      replace: true,
      to: "/requests/$requestId/edit",
    });
  }, [navigate, request?.isDraft, requestId]);

  const handleBack = useLeaveMbcFlow();

  const handleBackToChat = useCallback(() => {
    void navigate({
      replace: true,
      search: {
        brandId,
        designType,
        ["project-id"]: projectId,
        ["request-type"]: requestType,
        resourceId,
        threadId,
      },
      to: "/requests/mbc",
    });
  }, [
    brandId,
    designType,
    navigate,
    projectId,
    requestType,
    resourceId,
    threadId,
  ]);

  // There is no request-detail route in this app (it still lives in the
  // legacy portal), so the queue list is where a submitted request lands.
  const handleSubmitted = useCallback(() => {
    void navigate({
      search: { filter: "all", tab: "queued" },
      to: "/requests",
    });
  }, [navigate]);

  return (
    <Suspense>
      <ErrorBoundary>
        <MBCReview
          brandId={brandId}
          designTypeId={designType}
          onBack={handleBack}
          onBackToChat={handleBackToChat}
          onSubmitted={handleSubmitted}
          resourceId={resourceId}
          threadId={threadId}
        />
      </ErrorBoundary>
    </Suspense>
  );
}
